import * as signalR from '@microsoft/signalr';
import * as fabric from 'office-ui-fabric-react';
import { IDropdownOption } from 'office-ui-fabric-react';
import { createRef, FC, useEffect, useReducer, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as api from '../../../api/generated';
import { KendoLoading, LoadingProvider, useAuthentication, useSetLoading } from '../../../components';
import { ILocationItem } from '../../../components/custom/LocationTree';
import useDebounce from '../../../components/hooks/useDebounce';
import { ErrorWithMessage } from '../../../components/react-toastify';
import { useWindow } from '../../../components/windows/WindowContext';
import { QueuePriority, ticketState, TicketState } from '../../../enums';
import '../../../styles/queueManagement.scss';
import { expand, footerStyles, fullScreenStyles } from '../Helpers';
import { actions, IServiceStatistic, ITicket, LOAD_LOCATION_STATUS, LOAD_QUEUES, LOAD_SERVICES } from '../model';
import { initialState, reducer } from '../reducer';
import CompactView from './CompactView';
import { TicketsContextProvider } from './context/TicketsContext';
import Queue from './Queue';
import QueueManagementHeader from './QueueManagementHeader';
import SendToQueueModalWindow from './SendToQueueModalWindow';
import { ShowCautionModal } from './ShowCautionModal';
import { ShowErrorModal } from './ShowErrorModal';
import WorkplaceSelect from './WorkplaceSelect';

const QueueManagement: FC = () => {
	const audioEl = createRef<HTMLAudioElement>();
	const [state, dispatch] = useReducer(reducer, initialState);

	const { openWindow } = useWindow();

	const { username } = useAuthentication();

	const [error, setError] = useState<string | null>(null);

	const [allLocations, setAllLocations] = useState<ILocationItem[] | null>(null);
	const [locationStatus, setLocationStatus] = useState(0);

	const [locationId, setLocationId] = useState<number | null>(
		localStorage.getItem('locationId') ? parseInt(localStorage.getItem('locationId')!) : null
	);

	const [automationSettings, setAutomationSettings] = useState({
		callNext: localStorage.getItem('automatedCall') == 'true',
		disabled: false
	});
	const [presetingCaution, setPresetCaution] = useState(localStorage.getItem('presetCaution') == 'true');
	const [showNewCaution, setNewCaution] = useState(false);
	const [justCalledNew, setJustCalledNew] = useState(localStorage.getItem('automatedCall') == 'true');
	const [selectedTicket, setSelectedTicket] = useState<ITicket | null>(null);

	const [ticketsFilterTerm, setTicketsFilterTerm] = useState<string>('');

	const debouncedSearchTerm = useDebounce(ticketsFilterTerm, 1000);

	const [isLoading, setIsLoading] = useState(false);
	const setQmIsLoading = useSetLoading();

	const fetchData = async () => {
		try {
			setIsLoading(true);
			const resp = await api.administration.getLocations(null);
			setAllLocations(resp);
			if (locationId) {
				const [locationStatusResponse] = await Promise.all([api.queueManagement.getLocationStatus(locationId)]);

				if (locationStatusResponse) {
					localStorage.setItem(
						'activeTicket',
						locationStatusResponse.assignedTicket ? locationStatusResponse.assignedTicket?.id.toString() : ''
					);
					dispatch({ type: LOAD_LOCATION_STATUS, payload: locationStatusResponse });

					await filterTicketStatistics();

					const queuesResponse = await api.queueManagement.getQueuesByLocation(locationId!);
					if (queuesResponse) {
						dispatch({ type: LOAD_QUEUES, payload: queuesResponse });
					}
				}
			}
		} catch (err) {
			setError(err.error);
		} finally {
			setIsLoading(false);
		}
	};

	const filterTickets = async (newSelectedValue: IDropdownOption) => {
		const currQueueIndex = state.queues.findIndex((x) => x.id === newSelectedValue.key);
		const newQueues = [...state.queues];
		if (newSelectedValue.selected !== undefined) {
			await api.queueManagement.updateSelections({
				locationId: locationId!,
				queueId: newQueues[currQueueIndex].id,
				selected: newSelectedValue.selected
			});
			if (newSelectedValue.selected) {
				const newQueue = await api.queueManagement.getQueuedTickets({ queueId: newSelectedValue.key as number, locationId: locationId! });
				dispatch(actions.replaceQueues(newQueue!));
			} else {
				dispatch(actions.replaceQueues(newQueues[currQueueIndex]));
			}
			await filterTicketStatistics();
		}
	};

	const filterTicketStatistics = async () => {
		try {
			setIsLoading(true);
			if (locationId) {
				const allServicesReponse = await api.service.getLocationServices(locationId);
				if (allServicesReponse) {
					const statisticServices: IServiceStatistic[] = [];
					allServicesReponse.forEach((service) => {
						if (statisticServices.filter((x) => x.id == service.id).length == 0) {
							statisticServices.push({
								id: service.id!.toString(),
								count: 0,
								name: service.name,
								groupId: service.groupId
							});
						}
					});
					dispatch({ type: LOAD_SERVICES, payload: statisticServices });
				}
			}
		} catch (err) {
			setError(err.error);
		} finally {
			setIsLoading(false);
		}
	};

	const setAndUpdateOtherLocations = async (call: boolean | undefined, presetCaution?: boolean, showCaution?: boolean, calling?: boolean) => {
		setAutomationSettings({
			...automationSettings,
			callNext: call!
		});
		setJustCalledNew(calling ? true : false);
		setPresetCaution(presetCaution ? true : false);
		if (call != undefined) await api.queueManagement.updateOtherLocations({ locationId: locationId!, enabled: call });
	};

	const moveTicketToLocation = async (ticketId: number, newState: TicketState) => {
		try {
			setIsLoading(true);
			setSelectedTicket(null);
			if (ticketId === 0) {
				setQmIsLoading(false);
			} else {
				localStorage.setItem('activeTicket', ticketId.toString());
				await api.queueManagement.moveTicketToLocation({
					ticketId: ticketId,
					newState,
					modifiedBy: username,
					locationId: locationId!,
					externalAction: false,
					locationKey: null,
					updatedByUserId: null,
					updatedFromSystemId: 1
				});
			}
		} catch (ex) {
			toast.error(<ErrorWithMessage title="Nepavyko pakviesti bilieto" message={ex.error[0]} />);
			setQmIsLoading(false);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchData();
		const initializeSignalR = () => {
			const connection = new signalR.HubConnectionBuilder()
				.withUrl(`/queueManagement?locationId=${locationId}`)
				.withAutomaticReconnect([1000, 3000, 5000, 10000, 20000, 30000, 60000, 60000])
				.build();

			connection.on('onRemoveTicketFromLocation', (ticket: ITicket) => {
				dispatch(actions.removeAssignedTicket(null));
				if (ticket.state !== ticketState.Served) dispatch(actions.addTicketToQueue(ticket, true));
				setQmIsLoading(false);
			});

			connection.on('onAddTicketToQueue', (ticket: ITicket) => {
				dispatch(actions.addTicketToQueue(ticket, false));

				if (ticket.state == ticketState.Waiting && !localStorage.getItem('activeTicket')) {
					setAndUpdateOtherLocations(
						false,
						localStorage.getItem('presetCaution') == 'true',
						localStorage.getItem('presetCaution') == 'true',
						localStorage.getItem('automatedCall') == 'true' ? true : false
					);
					if (localStorage.getItem('presetCaution') == 'true' && ticket.state == ticketState.Waiting) {
						setNewCaution(true);
						document.getElementById('playButton')?.click();
					}
					if (localStorage.getItem('automatedCall') == 'true' && ticket.state == ticketState.Waiting) {
						const ticketIdString = localStorage.getItem('nextTicketId');
						moveTicketToLocation(ticketIdString ? parseInt(ticketIdString) : ticket.id, ticketState.Invited);
						localStorage.setItem('automatedCall', 'false');
					}
				}
				setQmIsLoading(false);
			});
			connection.on('onRemoveTicketFromQueue', (ticketId: number) => {
				dispatch(actions.removeTicketFromQueue(ticketId));
				setQmIsLoading(false);
			});

			connection.on('onAutomationEnabled', (enabled: boolean) => {
				setAutomationSettings({ ...automationSettings, disabled: enabled });
				localStorage.setItem('automatedCall', 'false');
			});

			//actions

			connection.on('onInvitedTicketAction', (ticket: ITicket) => {
				dispatch(actions.assignTicket(ticket, locationId));
				setQmIsLoading(false);
			});

			connection.on('onServicedTicketAction', (ticket: ITicket) => {
				dispatch(actions.assignTicket(ticket, locationId));
				setQmIsLoading(false);
			});

			connection.on('onServedTicketAction', (ticketId: number) => {
				dispatch(actions.removeAssignedTicket(ticketId));
				setQmIsLoading(false);
			});

			connection.on('onRemovedTicketAction', (ticket: ITicket) => {
				dispatch(actions.removeAssignedTicket(ticket.id));
				dispatch(actions.addTicketToQueue(ticket, false));
				setQmIsLoading(false);
			});

			connection.on('onStoppedTicketAction', (ticket: ITicket) => {
				dispatch(actions.removeAssignedTicket(ticket.id));

				dispatch(actions.updateTicketStateOrAddToQueue(ticket, ticketState.Stopped));
				setQmIsLoading(false);
			});

			connection.onreconnecting(() => {
				if (connection.state === signalR.HubConnectionState.Reconnecting) {
					setIsLoading(true);
				}
			});

			connection.onreconnected(() => {
				if (connection.state === signalR.HubConnectionState.Connected) {
					setIsLoading(false);
				}
			});

			connection.start().catch((ex) => setError(ex));
			return connection;
		};

		const connection = initializeSignalR();

		return () => {
			connection.stop();
		};
	}, []);

	const closeCaution = () => {
		setNewCaution(false);
	};

	const removeTicketFromLocation = async (ticketId: number, newState: TicketState) => {
		try {
			setIsLoading(true);
			localStorage.setItem('activeTicket', '');
			await api.queueManagement.removeTicketFromLocation({
				ticketId: ticketId,
				newState,
				modifiedBy: username,
				externalAction: false,
				updatedByUserId: null,
				updatedFromSystemId: 1
			});
		} catch (err) {
			setError(err.error);
		} finally {
			setIsLoading(false);
		}
	};

	const removeTicketFromQueue = async (ticketId: number, priority: QueuePriority) => {
		try {
			setIsLoading(true);
			setSelectedTicket(null);
			await api.queueManagement.removeTicketFromQueue({
				ticketId,
				modifiedBy: username,
				newState: ticketState.Removed,
				priority,
				updatedByUserId: null,
				updatedFromSystemId: 1
			});

			dispatch(actions.updateTicketState(ticketId, ticketState.Removed));
		} catch (err) {
			setError(err.error);
		} finally {
			setIsLoading(false);
		}
	};

	const sendTicketToAnotherQueue = async (ticketId: number, queueId: number, serviceId: number | null, queuePriority: QueuePriority) => {
		try {
			dispatch(actions.removeAssignedTicket(null));
			setSelectedTicket(null);
			await api.queueManagement.sendTicketToAnotherQueue({
				ticketId,
				queueId,
				serviceId: serviceId,
				modifiedBy: username,
				queuePriority,
				externalAction: null,
				updatedByUserId: null,
				updatedFromSystemId: 1
			});

			dispatch(actions.removeTicketFromQueue(ticketId));
		} catch (err) {
			setError(err.error);
		}
	};

	const openSendToQueueModalWindow = async (ticket: ITicket) => {
		try {
			SendToQueueModalWindow.defaultProps = {
				componentProps: {
					assignedTicket: ticket,
					sendTicketToAnotherQueue: (ticketId: number, queueId: number, serviceId: number | null, priority: QueuePriority) =>
						sendTicketToAnotherQueue(ticketId, queueId, serviceId, priority)
				}
			};
			openWindow({
				title: 'Siųsti į kitą eilę',
				component: SendToQueueModalWindow,
				isDialogBehaviour: true,
				width: 650,
				height: 400,
				onBeforeClose: () => setQmIsLoading(false)
			});
		} catch (ex) {
			toast.error(ex.error[0]);
		}
	};

	const changeLocationStatus = async (newState: number) => {
		try {
			setIsLoading(true);
			await api.display.updateLocationStatus({
				locationId: locationId!,
				locationStatus: newState
			});
		} catch (err) {
			setError(err.error);
		} finally {
			setIsLoading(false);
		}
	};

	if (!state.locationStatus) {
		return <KendoLoading />;
	}

	return (
		<>
			{isLoading && <KendoLoading />}
			<button style={{ display: 'none' }} id="playButton" onClick={() => audioEl.current!.play()}>
				Focus input
			</button>
			<audio ref={audioEl} src={`/doorbell.mp3`}></audio>
			<fabric.Stack styles={fullScreenStyles}>
				<QueueManagementHeader locationId={locationId} setLocationId={setLocationId} locations={allLocations} />
				<WorkplaceSelect locations={allLocations} />
				{locationId && (
					<fabric.Stack horizontal styles={expand}>
						<TicketsContextProvider queues={state.queues} services={state.services} debouncedSearchTerm={debouncedSearchTerm}>
							<CompactView
								assignedTicket={state.locationStatus.assignedTicket}
								moveTicketToLocation={moveTicketToLocation}
								removeTicketFromLocation={removeTicketFromLocation}
								changeLocationStatus={changeLocationStatus}
								services={state.services.filter((x) => x.id !== '0')}
								automatedCall={automationSettings.callNext}
								presetShowCaution={presetingCaution}
								disableAutomated={automationSettings.disabled}
								setAutomatedCall={setAndUpdateOtherLocations}
								openSendToQueueModalWindow={openSendToQueueModalWindow}
								setSelectedTicket={setSelectedTicket}
								locationStatus={locationStatus}
								setLocationStatus={setLocationStatus}
								allQueues={state.queues}
								filterTickets={filterTickets}
								ticketsFilterTerm={ticketsFilterTerm}
								setTicketsFilterTerm={setTicketsFilterTerm}
							/>
							<Queue
								assignedTicket={state.locationStatus.assignedTicket}
								moveTicketToLocation={moveTicketToLocation}
								removeTicketFromQueue={removeTicketFromQueue}
								openSendToQueueModalWindow={openSendToQueueModalWindow}
								setSelectedTicket={setSelectedTicket}
								selectedTicket={selectedTicket}
								setLocationStatus={setLocationStatus}
							/>
						</TicketsContextProvider>
					</fabric.Stack>
				)}
				<fabric.Stack styles={footerStyles}></fabric.Stack>
			</fabric.Stack>

			{showNewCaution && (
				<ShowCautionModal ticketNumber={state.locationStatus.assignedTicket?.number} autoCall={justCalledNew} setShowCaution={closeCaution} />
			)}
			{error && <ShowErrorModal logout={true} error={error}></ShowErrorModal>}
		</>
	);
};

export default (props: RouteComponentProps) => (
	<LoadingProvider>
		<QueueManagement {...props} />
	</LoadingProvider>
);
